import type { MouseEvent } from 'react'
import type { IconType } from 'react-icons'

import cx from 'classnames'
import { useRef, useState } from 'react'
import { MdMoreVert } from 'react-icons/md'
import { animated, config, useSpring } from 'react-spring'
import { useClickAway } from 'react-use'

import { useContainerMeasureProvider } from '../../claim-history/simple-table'
import { Divider } from '../Divider'

const ONE_BUTTON_HEIGHT = 44
const AVERAGE_SIZE = ONE_BUTTON_HEIGHT * 3
const MARGIN = 33

export interface Action {
  id: string
  icon?: IconType
  label: string
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
}

export interface ContextMenuProps {
  position?: 'top'
  actions?: Action[]
  direction?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right'
  onClose: () => void
}

export function ContextMenuButton (props: ContextMenuProps): JSX.Element {
  const [ state, setState ] = useState<{ visible: boolean }>({ visible: false })
  const ref = useRef<HTMLDivElement>(null)
  let containerContext = useContainerMeasureProvider()

  let optionsPosition = (ref?.current?.offsetTop ?? 0) + AVERAGE_SIZE + MARGIN

  let positionTop = containerContext
    ? optionsPosition >= (containerContext?.height ?? 0)
    : false

  return (
    <div
      ref={ref}
      role='button'
      tabIndex={0}
      className='cursor-pointer relative'
      onKeyDown={() => {
        // Do nothing
      }}
      onClick={event => {
        event.preventDefault()
        event.stopPropagation()

        setState(() => ({
          visible: true
        }))
      }}
      title='More options'
    >
      <MdMoreVert
        size={18}
        className='text-[#9CA3AF] hover:text-gray-600 -mr-2'
      />
      {state.visible && (
        <ContextMenu
          {...props}
          position={positionTop ? 'top' : undefined}
          onClose={() => {
            props.onClose?.()
            setState(prev => ({
              ...prev, visible: false
            }))
          }}
        />
      )}
    </div>
  )
}

export function ContextMenu ({ position, actions = [], onClose }: ContextMenuProps): JSX.Element | null {
  const ref = useRef<HTMLDivElement>(null)

  useClickAway(ref, () => {
    onClose()
  })

  let styles = useSpring({
    from: {
      opacity: 0,
      transform: `translate(-100%, ${position === 'top' ? '-80%' : '10%'})  scale(0.75)`
    },

    to: {
      opacity: 1,
      transform: `translate(-100%, ${position === 'top' ? '-80%' : '0%'})  scale(1)`
    },

    config: config.stiff
  })

  return (
    <animated.div
      className={cx('absolute top-0 w-full left-0 flex flex-col min-w-max overflow-hidden bg-white rounded-lg border border-gray-200 shadow-lg origin-top-left z-10')}
      ref={ref}
      style={styles}
    >
      {actions.map(action => (
        <div
          key={action.id}
          className='w-full'
        >
          <Button
            id={action.id}
            icon={action.icon}
            className='w-full'
            data-test-id={typeof action.id === 'string' && `context-menu-${action.id}`}
            onClick={(e) => {
              e.preventDefault()
              action.onClick?.(e)
              onClose?.()
            }}
          >
            {action.label}
          </Button>
          <Divider />
        </div>
      ))}
    </animated.div>
  )
}

ContextMenu.displayName = 'ContextMenu'

interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  icon?: IconType
}

function Button ({ icon: Icon, children, className, ...rest }: ButtonProps): JSX.Element {
  return (
    <button
      data-test-id={rest?.id ?? 'context-menu-unknown-btn'}
      type='button'
      className={cx('flex flex-row items-center px-4 py-2.5 text-sm font-roboto font-medium text-gray-800 hover:bg-gray-100', [ className ?? '' ])}
      {...rest}
    >
      {Icon && (
        <Icon
          size={18}
          className='text-gray-500'
        />
      )}

      <span className='ml-2 font-roboto text-sm font-normal'>
        {children}
      </span>
    </button>
  )
}
